import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Edit, Trash2, Plus, Save, X, Upload, AlertCircle, CheckCircle, Grid, List } from 'lucide-react';
import { Box, Fade, Paper, Typography, useTheme } from '@mui/material';
import axios from 'axios';
import { isAdminMode } from '../utils/auth'; // Authentication utility

interface FacilityDto {
    image: string;
    title: string;
    content: string;
}

const BASE_URL = import.meta.env.VITE_BASE_URL ?? '';

const FacilitiesComponent: React.FC = () => {
    const theme = useTheme();
    const adminMode = isAdminMode();

    // Shared state for both modes
    const [facilities, setFacilities] = useState<FacilityDto[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Carousel-specific state
    const [activeIndex, setActiveIndex] = useState(0);
    const [show, setShow] = useState(false);
    const carouselRef = useRef<HTMLDivElement>(null);

    // CRUD-specific state
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [formData, setFormData] = useState<FacilityDto>({
        image: '',
        title: '',
        content: ''
    });
    const [newImagePreview, setNewImagePreview] = useState<string>('');

    useEffect(() => {
        fetchFacilities();
    }, []);

    useEffect(() => {
        setShow(true);
    }, []);

    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => {
                setNotification(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    const fetchFacilities = async () => {
        try {
            setLoading(true);
            const response = await axios.get<FacilityDto[]>(`${BASE_URL}/api/facilities`);
            setFacilities(response.data);
            setError(null);
        } catch (err: any) {
            console.error('Failed to fetch facilities:', err);
            if (err.response?.status === 404) {
                setFacilities([]);
                setError(null);
            } else {
                setError("Failed to load facilities data. Please try again later.");
                if (adminMode) {
                    showNotification("Failed to load facilities data", 'error');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    // Carousel functions
    const prevCard = () => {
        setActiveIndex(prev => (prev === 0 ? facilities.length - 1 : prev - 1));
    };

    const nextCard = () => {
        setActiveIndex(prev => (prev === facilities.length - 1 ? 0 : prev + 1));
    };

    const handleWheel = (e: React.WheelEvent) => {
        if (!adminMode) {
            e.deltaY > 0 ? nextCard() : prevCard();
        }
    };

    useEffect(() => {
        if (!adminMode) {
            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'ArrowLeft') prevCard();
                if (e.key === 'ArrowRight') nextCard();
            };
            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }
    }, [facilities, adminMode]);

    // CRUD functions
    const showNotification = (message: string, type: 'success' | 'error') => {
        setNotification({ message, type });
    };

    const openCreateDialog = () => {
        if (!adminMode) return;

        setFormData({
            image: '',
            title: '',
            content: ''
        });
        setNewImagePreview('');
        setEditingIndex(null);
        setIsEditing(false);
        setShowDialog(true);
    };

    const openEditDialog = (facility: FacilityDto, index: number) => {
        if (!adminMode) return;

        setFormData({ ...facility });
        setNewImagePreview('');
        setEditingIndex(index);
        setIsEditing(true);
        setShowDialog(true);
    };

    const closeDialog = () => {
        setShowDialog(false);
        setFormData({
            image: '',
            title: '',
            content: ''
        });
        setNewImagePreview('');
        setEditingIndex(null);
        setIsEditing(false);
    };

    const handleInputChange = (field: keyof FacilityDto, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const result = e.target?.result as string;
                setNewImagePreview(result);
                setFormData(prev => ({
                    ...prev,
                    image: result
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        if (!adminMode) return;

        try {
            let updatedFacilities: FacilityDto[];

            if (isEditing && editingIndex !== null) {
                updatedFacilities = [...facilities];
                updatedFacilities[editingIndex] = formData;
            } else {
                updatedFacilities = [...facilities, formData];
            }

            const response = await fetch(`${BASE_URL}/api/facilities`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedFacilities),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            await fetchFacilities();
            closeDialog();
            showNotification(
                isEditing ? 'Facility updated successfully!' : 'Facility created successfully!',
                'success'
            );
        } catch (err) {
            console.error('Error saving facility:', err);
            showNotification('Failed to save facility. Please try again.', 'error');
        }
    };

    const handleDelete = async (index: number) => {
        if (!adminMode) return;

        if (!window.confirm('Are you sure you want to delete this facility?')) {
            return;
        }

        try {
            const updatedFacilities = facilities.filter((_, i) => i !== index);

            if (updatedFacilities.length === 0) {
                const response = await fetch(`${BASE_URL}/api/facilities`, {
                    method: 'DELETE',
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                setFacilities([]);
            } else {
                const response = await fetch(`${BASE_URL}/api/facilities`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(updatedFacilities),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                await fetchFacilities();
            }

            showNotification('Facility deleted successfully!', 'success');
        } catch (err) {
            console.error('Error deleting facility:', err);
            showNotification('Failed to delete facility. Please try again.', 'error');
        }
    };

    const handleDeleteAll = async () => {
        if (!adminMode) return;

        if (!window.confirm('Are you sure you want to delete ALL facilities? This action cannot be undone.')) {
            return;
        }

        try {
            const response = await fetch(`${BASE_URL}/api/facilities`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            setFacilities([]);
            showNotification('All facilities deleted successfully!', 'success');
        } catch (err) {
            console.error('Error deleting all facilities:', err);
            showNotification('Failed to delete all facilities. Please try again.', 'error');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex justify-center items-center">
                <div className="relative">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 shadow-lg"></div>
                    <div className="absolute inset-0 rounded-full bg-white/20 animate-pulse"></div>
                </div>
            </div>
        );
    }

    // User Mode - Modern Carousel UI
    if (!adminMode) {
        if (facilities.length === 0) {
            return (
                <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
                    <Typography className="text-slate-600 text-xl font-medium">No facilities available...</Typography>
                </div>
            );
        }

        const totalCards = facilities.length;

        return (
            <div className="py-10 flex justify-center items-center w-full">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 3, sm: 6 },
                        mx: 'auto',
                        my: 0,
                        backgroundColor: 'transparent',
                    }}
                    className={`md:w-[80%] w-[90%] transition-all duration-1000 ease-out ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                >
                    <Fade in timeout={800}>
                        <Box className="text-center mb-12">
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                                    fontWeight: 800,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    mb: 3,
                                    letterSpacing: '-0.02em'
                                }}
                            >
                                Facilities
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'text.secondary',
                                    fontSize: '1.1rem',
                                    maxWidth: '600px',
                                    mx: 'auto',
                                    lineHeight: 1.6,
                                    fontWeight: 400
                                }}
                            >
                                Here you can add facilities and view the facilities.
                            </Typography>
                        </Box>
                    </Fade>
                    {/* Enhanced Carousel */}
                    <div
                        className="w-full py-16 relative overflow-hidden"
                        onWheel={handleWheel}
                        ref={carouselRef}
                        tabIndex={0}
                    >
                        {/* Modern Navigation Buttons */}
                        <button
                            onClick={prevCard}
                            className="absolute left-8 top-1/2 transform -translate-y-1/2 z-50 bg-white/90 backdrop-blur-sm hover:bg-white hover:scale-110 p-4 rounded-full shadow-2xl border border-white/20 transition-all duration-300 group"
                        >
                            <ChevronLeft size={24} className="text-slate-700 group-hover:text-blue-600 transition-colors" />
                        </button>

                        <button
                            onClick={nextCard}
                            className="absolute right-8 top-1/2 transform -translate-y-1/2 z-50 bg-white/90 backdrop-blur-sm hover:bg-white hover:scale-110 p-4 rounded-full shadow-2xl border border-white/20 transition-all duration-300 group"
                        >
                            <ChevronRight size={24} className="text-slate-700 group-hover:text-blue-600 transition-colors" />
                        </button>

                        {/* Enhanced Cards */}
                        <div className="relative flex justify-center h-[600px] w-full">
                            {facilities.map((card, i) => {
                                const distanceFromActive = ((i - activeIndex + totalCards) % totalCards);
                                let adjustedDistance = distanceFromActive <= totalCards / 2
                                    ? distanceFromActive
                                    : distanceFromActive - totalCards;
                                const zIndex = totalCards - Math.abs(adjustedDistance);
                                const translateX = adjustedDistance * 180;
                                const scale = 1 - Math.min(Math.abs(adjustedDistance) * 0.08, 0.4);
                                const isActive = i === activeIndex;

                                return (
                                    <div
                                        key={i}
                                        className="absolute transition-all duration-700 cursor-pointer"
                                        style={{
                                            transform: `translateX(${translateX}px) scale(${scale})`,
                                            zIndex,
                                            width: '420px',
                                            height: '600px',
                                        }}
                                        onClick={() => setActiveIndex(i)}
                                    >
                                        <div className={`relative h-full w-full rounded-3xl overflow-hidden group shadow-2xl border border-white/20 backdrop-blur-sm ${isActive ? 'ring-4 ring-blue-500/50' : ''
                                            }`}>
                                            <div
                                                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                                                style={{ backgroundImage: `url(${card.image || "/api/placeholder/420/600"})` }}
                                            />

                                            {/* Modern Gradient Overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-all duration-500 group-hover:from-black/60" />

                                            {/* Enhanced Content */}
                                            <div className="absolute inset-0 flex flex-col justify-end p-8 transition-all duration-500">
                                                <div className={`transform transition-all duration-700 ${isActive ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-70'
                                                    }`}>
                                                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                                                        <h2 className="text-3xl font-bold text-white mb-4 leading-tight">
                                                            {card.title}
                                                        </h2>
                                                        <div className={`overflow-hidden transition-all duration-700 ${isActive && i === activeIndex
                                                                ? 'max-h-48 opacity-100'
                                                                : 'max-h-0 opacity-0'
                                                            }`}>
                                                            <p className="text-white/90 text-lg leading-relaxed font-light">
                                                                {card.content}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Active Indicator */}
                                            {isActive && (
                                                <div className="absolute top-6 right-6 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                                                    Featured
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Modern Dots Indicator */}
                        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 bg-white/20 backdrop-blur-md rounded-full p-4 border border-white/30">
                            {facilities.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveIndex(i)}
                                    className={`transition-all duration-300 rounded-full ${i === activeIndex
                                            ? 'w-8 h-3 bg-blue-500 shadow-lg'
                                            : 'w-3 h-3 bg-white/60 hover:bg-white/80'
                                        }`}
                                />
                            ))}
                        </div>
                    </div>
                </Paper>
            </div>
        );
    }

    // Admin Mode - Modern CRUD Management UI
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
            <div className="py-8 px-4 sm:px-8 max-w-7xl mx-auto">
                {/* Enhanced Notification */}
                {notification && (
                    <div className={`fixed top-6 right-6 z-50 p-4 rounded-2xl shadow-2xl flex items-center backdrop-blur-md border transform transition-all duration-300 ${notification.type === 'success'
                            ? 'bg-emerald-50/90 text-emerald-800 border-emerald-200'
                            : 'bg-red-50/90 text-red-800 border-red-200'
                        }`}>
                        {notification.type === 'success' ? (
                            <CheckCircle className="w-5 h-5 mr-3 text-emerald-600" />
                        ) : (
                            <AlertCircle className="w-5 h-5 mr-3 text-red-600" />
                        )}
                        <span className="font-medium">{notification.message}</span>
                    </div>
                )}

                {/* Modern Header */}
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12 gap-6">
                    <div>
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                                <Grid className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                                    Facilities Management
                                </h1>
                                <p className="text-slate-600 mt-1 font-medium">Manage and organize school facilities</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Enhanced View Mode Toggle */}
                        <div className="flex bg-white rounded-xl p-2 shadow-lg border border-slate-200">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-3 rounded-lg transition-all duration-200 ${viewMode === 'grid'
                                        ? 'bg-blue-500 text-white shadow-md'
                                        : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                            >
                                <Grid className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-3 rounded-lg transition-all duration-200 ${viewMode === 'list'
                                        ? 'bg-blue-500 text-white shadow-md'
                                        : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                            >
                                <List className="w-5 h-5" />
                            </button>
                        </div>

                        {facilities.length > 0 && (
                            <button
                                onClick={handleDeleteAll}
                                className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
                            >
                                Delete All
                            </button>
                        )}

                        <button
                            onClick={openCreateDialog}
                            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-8 py-3 rounded-xl flex items-center font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Add Facility
                        </button>
                    </div>
                </div>

                {/* Enhanced Error State */}
                {error && (
                    <div className="bg-red-50 border-l-4 border-red-400 text-red-800 px-6 py-4 rounded-xl flex items-center mb-8 shadow-lg">
                        <AlertCircle className="w-6 h-6 mr-3 text-red-600" />
                        <span className="font-medium">{error}</span>
                    </div>
                )}

                {/* Enhanced Empty State */}
                {facilities.length === 0 && !error && (
                    <div className="text-center py-24">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl w-32 h-32 mx-auto mb-8 flex items-center justify-center shadow-lg border border-blue-100">
                            <Plus className="w-16 h-16 text-blue-400" />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-800 mb-4">No facilities found</h3>
                        <p className="text-slate-600 mb-8 text-lg max-w-md mx-auto">Get started by adding your first facility to showcase your school's amenities</p>
                        <button
                            onClick={openCreateDialog}
                            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
                        >
                            Add First Facility
                        </button>
                    </div>
                )}

                {/* Enhanced Facilities Display */}
                {facilities.length > 0 && (
                    <div className={viewMode === 'grid'
                        ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8"
                        : "space-y-6"
                    }>
                        {facilities.map((facility, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-slate-100 hover:scale-[1.02] ${viewMode === 'list' ? 'flex' : ''
                                    }`}
                            >
                                <div className={viewMode === 'list' ? 'w-64 flex-shrink-0' : 'w-full'}>
                                    <img
                                        src={facility.image}
                                        alt={facility.title}
                                        className={`object-cover transition-transform duration-300 hover:scale-110 ${viewMode === 'list' ? 'h-full w-full' : 'w-full h-56'
                                            }`}
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.src = '/api/placeholder/400/300';
                                        }}
                                    />
                                </div>

                                <div className={`p-6 ${viewMode === 'list' ? 'flex-1 flex flex-col' : ''}`}>
                                    <h3 className="text-xl font-bold text-slate-800 mb-3 leading-tight">
                                        {facility.title}
                                    </h3>
                                    <p className={`text-slate-600 leading-relaxed ${viewMode === 'list' ? 'flex-1' : 'mb-6'} line-clamp-3`}>
                                        {facility.content}
                                    </p>

                                    <div className={`flex gap-3 ${viewMode === 'list' ? 'mt-6' : ''}`}>
                                        <button
                                            onClick={() => openEditDialog(facility, index)}
                                            className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-3 rounded-xl flex items-center justify-center font-semibold transition-all duration-200 hover:scale-105"
                                        >
                                            <Edit className="w-4 h-4 mr-2" />
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(index)}
                                            className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 px-4 py-3 rounded-xl flex items-center justify-center font-semibold transition-all duration-200 hover:scale-105"
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Enhanced Dialog */}
                {showDialog && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-slate-200">
                            {/* Enhanced Dialog Header */}
                            <div className="flex justify-between items-center p-8 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                                <div>
                                    <h2 className="text-3xl font-bold text-slate-800">
                                        {isEditing ? 'Edit Facility' : 'Create New Facility'}
                                    </h2>
                                    <p className="text-slate-600 mt-1">
                                        {isEditing ? 'Update facility information' : 'Add a new facility to showcase'}
                                    </p>
                                </div>
                                <button
                                    onClick={closeDialog}
                                    className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-xl transition-all duration-200"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Enhanced Dialog Content */}
                            <div className="p-8 space-y-8">
                                {/* Title Input */}
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-3">
                                        Facility Title
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50 focus:bg-white"
                                        placeholder="e.g., Modern Biology Laboratory"
                                    />
                                </div>

                                {/* Content Input */}
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-3">
                                        Description
                                    </label>
                                    <textarea
                                        value={formData.content}
                                        onChange={(e) => handleInputChange('content', e.target.value)}
                                        rows={4}
                                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50 focus:bg-white resize-vertical"
                                        placeholder="Describe the facility and its features..."
                                    />
                                </div>

                                {/* Enhanced Image Section */}
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-3">
                                        Facility Image
                                    </label>

                                    {/* Current Image Preview */}
                                    {isEditing && !newImagePreview && formData.image && (
                                        <div className="mb-6">
                                            <p className="text-sm text-slate-600 mb-3 font-medium">Current Image:</p>
                                            <img
                                                src={formData.image}
                                                alt="Current"
                                                className="w-full h-56 object-cover rounded-xl border-2 border-slate-200 shadow-lg"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = '/api/placeholder/400/300';
                                                }}
                                            />
                                        </div>
                                    )}

                                    {/* New Image Preview */}
                                    {newImagePreview && (
                                        <div className="mb-6">
                                            <p className="text-sm text-blue-600 mb-3 font-semibold">New Image Preview:</p>
                                            <img
                                                src={newImagePreview}
                                                alt="New preview"
                                                className="w-full h-56 object-cover rounded-xl border-2 border-blue-300 shadow-lg"
                                            />
                                        </div>
                                    )}

                                    {/* Enhanced File Upload */}
                                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center mb-6 bg-slate-50 hover:bg-slate-100 transition-colors duration-200">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="hidden"
                                            id="image-upload"
                                        />
                                        <label
                                            htmlFor="image-upload"
                                            className="cursor-pointer inline-flex items-center px-6 py-3 border-2 border-slate-300 rounded-xl shadow-sm text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 hover:border-blue-400 hover:text-blue-600 transition-all duration-200"
                                        >
                                            <Upload className="w-5 h-5 mr-2" />
                                            {isEditing ? 'Upload New Image' : 'Upload Image'}
                                        </label>
                                        <p className="text-sm text-slate-500 mt-3">
                                            Supported formats: JPG, PNG, WebP (Max 5MB)
                                        </p>
                                    </div>

                                    {/* Enhanced URL Input */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-3">
                                            Or enter image URL
                                        </label>
                                        <input
                                            type="url"
                                            value={newImagePreview ? '' : formData.image}
                                            onChange={(e) => {
                                                handleInputChange('image', e.target.value);
                                                setNewImagePreview('');
                                            }}
                                            disabled={!!newImagePreview}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50 focus:bg-white disabled:bg-slate-100 disabled:cursor-not-allowed"
                                            placeholder="https://example.com/facility-image.jpg"
                                        />
                                        {newImagePreview && (
                                            <p className="text-sm text-slate-500 mt-2 font-medium">
                                                Clear the uploaded image above to use URL instead
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Enhanced Dialog Actions */}
                            <div className="flex justify-end gap-4 p-8 border-t border-slate-200 bg-gradient-to-r from-white to-slate-50">
                                <button
                                    onClick={closeDialog}
                                    className="px-6 py-3 text-slate-700 border-2 border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={!formData.title || !formData.content || !formData.image}
                                    className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-500 disabled:cursor-not-allowed transition-all duration-200 flex items-center font-semibold shadow-lg hover:shadow-xl disabled:shadow-none hover:scale-105 disabled:scale-100"
                                >
                                    <Save className="w-5 h-5 mr-2" />
                                    {isEditing ? 'Update Facility' : 'Create Facility'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FacilitiesComponent;